import { useCallback, useReducer, useRef } from 'react';
import type { Deck } from '@allemnii-pulse/shared';

const MAX_HISTORY = 100;
/** Edits sharing a coalesce key this close together are one undo step (a typing burst, a run of
 * arrow-key nudges). Each edit restarts the window, so continuous typing stays one step. */
const COALESCE_MS = 1500;

/**
 * Undo/redo over a controlled deck. The deck itself lives with the parent; this keeps the
 * snapshots before each committed edit. Drags and resizes commit once, on release, so they're a
 * single step without needing a key. Stacks are refs (not state) so onChange is never called
 * from inside a state updater, which StrictMode would run twice.
 */
export function useDeckHistory(deck: Deck, onChange: (deck: Deck) => void) {
  const deckRef = useRef(deck);
  // Follow the prop when the parent replaces the deck itself (e.g. loading a restored version).
  const lastProp = useRef(deck);
  if (deck !== lastProp.current) {
    lastProp.current = deck;
    deckRef.current = deck;
  }
  const past = useRef<Deck[]>([]);
  const future = useRef<Deck[]>([]);
  const lastKey = useRef<{ key: string; at: number } | null>(null);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const commit = useCallback(
    (next: Deck, coalesceKey?: string) => {
      const now = Date.now();
      const coalesce =
        !!coalesceKey &&
        lastKey.current?.key === coalesceKey &&
        now - lastKey.current.at < COALESCE_MS;
      if (!coalesce) past.current = [...past.current, deckRef.current].slice(-MAX_HISTORY);
      future.current = [];
      lastKey.current = coalesceKey ? { key: coalesceKey, at: now } : null;
      deckRef.current = next;
      onChange(next);
      rerender();
    },
    [onChange],
  );

  const undo = useCallback(() => {
    const prev = past.current[past.current.length - 1];
    if (!prev) return;
    past.current = past.current.slice(0, -1);
    future.current = [deckRef.current, ...future.current].slice(0, MAX_HISTORY);
    lastKey.current = null;
    deckRef.current = prev;
    onChange(prev);
    rerender();
  }, [onChange]);

  const redo = useCallback(() => {
    const next = future.current[0];
    if (!next) return;
    future.current = future.current.slice(1);
    past.current = [...past.current, deckRef.current].slice(-MAX_HISTORY);
    lastKey.current = null;
    deckRef.current = next;
    onChange(next);
    rerender();
  }, [onChange]);

  /** The deck as of the last commit — ahead of the `deck` prop until the parent re-renders, so
   * edits made in quick succession (or after an await) build on each other. */
  const current = useCallback(() => deckRef.current, []);

  return {
    commit,
    undo,
    redo,
    current,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}
